import * as pc from 'playcanvas';

const canvas = document.querySelector('#game');
const ui = Object.fromEntries([
  'loading','load-text','letterbox','skip','objective','hotspot','hotspot-label','subtitle',
  'speaker','subtitle-line','reveal','debug','completion','completion-kicker',
  'completion-title','completion-body','restart','language','audio-btn'
].map(id => [id.replaceAll('-', '_'), document.querySelector(`#${id}`)]));

const params = new URLSearchParams(location.search);
let language = params.get('lang') || localStorage.getItem('departure-language') || 'en';
if (!['en','nl','es'].includes(language)) language = 'en';
ui.language.value = language;
let locale = {};
const tr = key => locale[key] ?? key;

async function loadLocale(code) {
  try {
    const response = await fetch(`${import.meta.env.BASE_URL}locales/${code}.json`);
    if (!response.ok) throw new Error(`locale ${response.status}`);
    locale = await response.json();
  } catch (error) {
    console.warn('Locale fallback:', error);
    if (code !== 'en') return loadLocale('en');
  }
  document.documentElement.lang = code;
  document.title = `Departure Platform — PlayCanvas`;
  ui.load_text.textContent = tr('ui.loading');
  ui.skip.textContent = tr('ui.skip');
  ui.objective.textContent = tr('ui.objective');
  ui.hotspot_label.textContent = `${tr('action.use')} · ${tr('hotspot.elevator')}`;
  ui.speaker.textContent = tr('subtitle.system');
  ui.completion_kicker.textContent = tr('completion.kicker');
  ui.completion_title.textContent = tr('completion.title');
  ui.completion_body.textContent = tr('completion.body');
  ui.restart.textContent = tr('ui.restart');
  ui.audio_btn.textContent = audio.enabled ? tr('ui.audioOn') : tr('ui.audioOff');
}

const audio = {
  ctx: null, master: null, enabled: false,
  start() {
    if (this.enabled) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx ||= new AC();
    this.ctx.resume();
    this.master = this.ctx.createGain();
    this.master.gain.value = .12;
    this.master.connect(this.ctx.destination);
    const hum = this.ctx.createOscillator();
    const humGain = this.ctx.createGain();
    hum.type = 'sine'; hum.frequency.value = 48; humGain.gain.value = .13;
    hum.connect(humGain).connect(this.master); hum.start();
    const lfo = this.ctx.createOscillator(), lfoGain = this.ctx.createGain();
    lfo.frequency.value = .18; lfoGain.gain.value = 12;
    lfo.connect(lfoGain).connect(hum.frequency); lfo.start();
    const length = this.ctx.sampleRate * 2;
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i=0;i<length;i++) { const white=Math.random()*2-1; last=.985*last+.015*white; data[i]=last*1.8; }
    const wind = this.ctx.createBufferSource(), windGain=this.ctx.createGain();
    wind.buffer=buffer; wind.loop=true; windGain.gain.value=.11;
    wind.connect(windGain).connect(this.master); wind.start();
    this.enabled = true;
    ui.audio_btn.textContent = tr('ui.audioOn');
  },
  beep(freq=520, duration=.08, gain=.12) {
    if (!this.enabled) return;
    const o=this.ctx.createOscillator(), g=this.ctx.createGain(), now=this.ctx.currentTime;
    o.frequency.value=freq; o.type='sine'; g.gain.setValueAtTime(gain,now);
    g.gain.exponentialRampToValueAtTime(.001,now+duration);
    o.connect(g).connect(this.master); o.start(now); o.stop(now+duration);
  },
  step() { this.beep(92,.045,.065); }
};

await loadLocale(language);

const app = new pc.Application(canvas, {
  graphicsDeviceOptions: {
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: params.has('capture')
  },
  mouse: new pc.Mouse(canvas),
  touch: new pc.TouchDevice(canvas)
});
app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
app.setCanvasResolution(pc.RESOLUTION_AUTO, Math.min(devicePixelRatio, 1.5));
app.graphicsDevice.maxPixelRatio = Math.min(devicePixelRatio, 1.5);
app.scene.ambientLight = new pc.Color(.065,.115,.14);
app.scene.fog.type = pc.FOG_LINEAR;
app.scene.fog.color = new pc.Color(.025,.085,.105);
app.scene.fog.start = 24;
app.scene.fog.end = 72;
app.start();

const quality = innerWidth < 900 || params.get('quality') === 'low' ? 'low' : 'high';
const mats = {};
function material(name, color, emissive=null, opacity=1, metal=.15) {
  const m = new pc.StandardMaterial();
  m.name=name; m.diffuse=color; m.metalness=metal; m.gloss=metal>.3?.65:.32;
  if (emissive) { m.emissive=emissive; m.emissiveIntensity=2; }
  if (opacity < 1) { m.opacity=opacity; m.blendType=pc.BLEND_NORMAL; m.depthWrite=false; }
  m.update(); mats[name]=m; return m;
}
const MAT = {
  steel: material('platform steel',new pc.Color(.075,.13,.15),null,1,.65),
  dark: material('dark structure',new pc.Color(.025,.045,.05),null,1,.5),
  edge: material('painted edge',new pc.Color(.46,.19,.055),new pc.Color(.2,.035,.006),1,.35),
  orange: material('spacesuit orange',new pc.Color(.9,.23,.045),null,1,.15),
  black: material('suit joints',new pc.Color(.025,.035,.037),null,1,.25),
  skin: material('skin',new pc.Color(.55,.29,.2)),
  glass: material('visor',new pc.Color(.08,.28,.31),new pc.Color(.015,.09,.1),.72,.7),
  white: material('rocket ceramic',new pc.Color(.56,.62,.61),null,1,.4),
  red: material('warning red',new pc.Color(.5,.03,.012),new pc.Color(1,.025,.003),1,.2),
  cyan: material('cyan strip',new pc.Color(.01,.16,.18),new pc.Color(.01,.7,.72),1,.25),
  steam: material('steam',new pc.Color(.37,.55,.57),new pc.Color(.02,.04,.04),.12,0)
};
function primitive(name,type,pos,scale,mat,parent=app.root,euler=null) {
  parent ||= app.root;
  const e=new pc.Entity(name); e.addComponent('render',{type}); e.setLocalPosition(...pos); e.setLocalScale(...scale);
  if(euler)e.setLocalEulerAngles(...euler);
  e.render.meshInstances.forEach(mi=>{mi.material=mat;mi.castShadow=quality==='high';mi.receiveShadow=true;});
  parent.addChild(e); return e;
}
function group(name,pos=[0,0,0],parent=app.root){const e=new pc.Entity(name);e.setLocalPosition(...pos);parent.addChild(e);return e;}
function light(name,type,color,intensity,pos,euler=[0,0,0],range=20,shadows=false) {
  const e=group(name,pos); e.setEulerAngles(...(euler??[0,0,0]));
  e.addComponent('light',{type,color,intensity,range,castShadows:shadows&&quality==='high',shadowResolution:1024,shadowBias:.25,normalOffsetBias:.18});
  return e;
}

// Layered, deliberately wide launch platform.
primitive('lower abyss','box',[5,-2.6,1],[58,4,.6],MAT.dark);
primitive('walkway','box',[0,0,0],[42,.42,4.2],MAT.steel);
primitive('walkway orange edge','box',[0,.23,2.02],[42,.08,.12],MAT.edge);
primitive('walkway light strip','box',[0,.24,-1.92],[42,.045,.06],MAT.cyan);
for(let x=-20;x<=20;x+=2) {
  primitive('deck seam','box',[x,.235,0],[.035,.018,3.9],MAT.dark);
  primitive('under beam','box',[x,-1.15,0],[.22,2.4,.25],MAT.dark,null,[0,0,x%4===0?8:-8]);
}
for(let x=-20;x<=20;x+=3.2) {
  primitive('rail post','box',[x,1,-1.82],[.07,1.55,.07],MAT.steel);
}
primitive('rail','box',[0,1.7,-1.82],[42,.07,.07],MAT.steel);
primitive('rail mid','box',[0,1.04,-1.82],[42,.045,.045],MAT.steel);

// Rocket and gantry in distant z layer.
const rocket=group('rocket',[12,1,-13]);
primitive('rocket body','cylinder',[0,10,0],[3,17,3],MAT.white,rocket);
primitive('rocket dark ring','cylinder',[0,3.2,0],[3.15,.55,3.15],MAT.dark,rocket);
primitive('rocket orange ring','cylinder',[0,8,0],[3.08,.18,3.08],MAT.edge,rocket);
primitive('rocket nose','cone',[0,19,0],[3.05,8,3.05],MAT.white,rocket);
for(const x of [-2.4,2.4]) primitive('booster','cylinder',[x,4,.2],[1.12,10,1.12],MAT.white,rocket);
for(const x of [-2.4,2.4]) primitive('engine glow','cylinder',[x,-1,0],[.75,.3,.75],MAT.red,rocket);
const tower=group('gantry',[18,0,-12]);
for(const x of [-2.8,2.8]) for(const z of [-1.5,1.5]) primitive('tower column','box',[x,10,z],[.35,22,.35],MAT.dark,tower);
for(let y=1;y<22;y+=2.6) {
  primitive('gantry floor','box',[0,y,0],[7,.18,4],MAT.steel,tower);
  primitive('gantry lamp','sphere',[-2.9,y+.3,1.6],[.18,.18,.18],MAT.red,tower);
}
for(let y=0;y<21;y+=5.2) {
  primitive('crossbrace','box',[0,y+2.5,1.51],[6.4,.14,.14],MAT.steel,tower,[0,0,25]);
  primitive('crossbrace','box',[0,y+2.5,1.52],[6.4,.14,.14],MAT.steel,tower,[0,0,-25]);
}
light('moon fill','directional',new pc.Color(.26,.54,.67),1.25,[0,10,0],[55,-32,0],40,true);
light('walkway warm','omni',new pc.Color(1,.23,.055),2.4,[12,3,1],null,14,false);
light('elevator cool','omni',new pc.Color(.03,.55,.62),2.2,[17,3,2],null,11,false);

// Elevator cage and independently animated doors.
const lift=group('launch elevator',[17,.25,0]);
primitive('lift floor','box',[0,.05,0],[4,.25,3.6],MAT.dark,lift);
primitive('lift roof','box',[0,4.8,0],[4,.25,3.6],MAT.steel,lift);
for(const x of [-1.9,1.9]) for(const z of [-1.65,1.65]) primitive('lift frame','box',[x,2.4,z],[.18,5,.18],MAT.steel,lift);
primitive('lift rear','box',[0,2.4,-1.68],[3.7,4.6,.12],MAT.dark,lift);
const doorL=primitive('left lift door','box',[-1.42,2.35,1.65],[1.42,4.45,.12],MAT.steel,lift);
const doorR=primitive('right lift door','box',[1.42,2.35,1.65],[1.42,4.45,.12],MAT.steel,lift);
primitive('lift overhead sign','box',[0,4.35,1.78],[2.7,.38,.06],MAT.edge,lift);
primitive('lift status lamp','sphere',[0,4.35,1.88],[.12,.12,.12],MAT.cyan,lift);

// Primitive articulated hero.
const hero=group('crew member',[-19,.25,.5]);
primitive('torso','capsule',[0,1.55,0],[.72,1.45,.55],MAT.orange,hero);
primitive('life support','box',[0,1.62,-.42],[.72,.9,.28],MAT.dark,hero);
primitive('waist','box',[0,.92,0],[.62,.25,.48],MAT.black,hero);
const head=primitive('head','sphere',[0,2.65,0],[.48,.58,.46],MAT.skin,hero);
primitive('hair','sphere',[0,2.91,-.03],[.5,.22,.46],MAT.dark,hero);
const helmet=primitive('helmet','sphere',[0,3.42,.05],[.66,.7,.62],MAT.white,hero);
const visor=primitive('visor','sphere',[0,3.4,.38],[.48,.42,.24],MAT.glass,hero);
const limb=(name,pos,parent=hero)=>{
  const pivot=group(`${name} pivot`,pos,parent);
  primitive(name,'capsule',[0,-.48,0],[.28,1.08,.28],name.includes('leg')?MAT.orange:MAT.orange,pivot);
  primitive(`${name} joint`,'sphere',[0,-.98,0],[.28,.28,.28],MAT.black,pivot);
  return pivot;
};
const leftArm=limb('left arm',[-.46,2.03,0]),rightArm=limb('right arm',[.46,2.03,0]);
const leftLeg=limb('left leg',[-.25,.82,0]),rightLeg=limb('right leg',[.25,.82,0]);
helmet.enabled=visor.enabled=false;

// Procedural steam puffs.
const puffs=[];
for(let i=0;i<(quality==='high'?18:9);i++) {
  const side=i%2?-1:1;
  const e=primitive('steam puff','sphere',[12+side*(2.4+Math.random()),.3+Math.random()*4,-10+Math.random()*2],[.3,.3,.3],MAT.steam);
  puffs.push({e,phase:Math.random()*7,speed:.25+Math.random()*.35,side});
}

const camera=group('cinematic camera',[-12,6.4,17]);
camera.addComponent('camera',{clearColor:new pc.Color(.018,.055,.07),fov:48,nearClip:.1,farClip:110});
camera.lookAt(new pc.Vec3(-9,2,0));

const State=Object.freeze({
  INTRO_WALK:'intro-walk', HELMET:'helmet', INTRO_CONTINUE:'intro-continue',
  PLAYER:'player-control', WALK_TO_LIFT:'walk-to-lift', ENTER:'lift-entry',
  DOORS:'doors-closing', ASCENT:'ascent', COMPLETE:'complete'
});
let state=State.INTRO_WALK, stateTime=0, elapsed=0, speedBoost=1, hover=false, reveal=false;
document.body.dataset.gameState=state;
let lastStep=0, subtitleUntil=0, fpsSmooth=60, frames=0, statTime=0;
const elevatorBox={min:new pc.Vec3(14.8,.1,-2),max:new pc.Vec3(19.2,5.3,2.4)};
const targetX=15.8;
function setState(next) {
  state=next; stateTime=0;
  document.body.dataset.gameState=state;
  if(next===State.HELMET) showSubtitle('subtitle.arrival',4);
  if(next===State.PLAYER) {
    ui.skip.style.display='none'; ui.letterbox.classList.add('play'); ui.objective.style.opacity='1';
    showSubtitle('subtitle.ready',3.6);
  }
  if(next===State.DOORS) audio.beep(220,.3,.15);
  if(next===State.ASCENT) audio.beep(85,1.2,.2);
  if(next===State.COMPLETE) ui.completion.classList.add('show');
}
function showSubtitle(key,duration=3) {
  ui.speaker.textContent=tr('subtitle.system'); ui.subtitle_line.textContent=tr(key);
  ui.subtitle.style.display='block'; subtitleUntil=elapsed+duration;
}
function setHeroX(x){hero.setPosition(x,hero.getPosition().y,hero.getPosition().z);}
function walkTo(x,speed,dt) {
  const p=hero.getPosition(), delta=x-p.x, step=Math.sign(delta)*Math.min(Math.abs(delta),speed*dt);
  setHeroX(p.x+step);
  const cadence=Math.sin(elapsed*speed*3.2);
  leftLeg.setLocalEulerAngles(cadence*24,0,0);rightLeg.setLocalEulerAngles(-cadence*24,0,0);
  leftArm.setLocalEulerAngles(-cadence*18,0,0);rightArm.setLocalEulerAngles(cadence*18,0,0);
  if(elapsed-lastStep>.42/speedBoost){audio.step();lastStep=elapsed;}
  return Math.abs(delta)<.025;
}
function idlePose(){leftLeg.setLocalEulerAngles(0,0,0);rightLeg.setLocalEulerAngles(0,0,0);leftArm.setLocalEulerAngles(0,0,0);rightArm.setLocalEulerAngles(0,0,0);}
function skipIntro() {
  if([State.INTRO_WALK,State.HELMET,State.INTRO_CONTINUE].includes(state)){
    setHeroX(7); helmet.enabled=visor.enabled=true; idlePose(); setState(State.PLAYER);
  }
}
function beginLiftInteraction() {
  if (state !== State.PLAYER) return false;
  setState(State.WALK_TO_LIFT);
  ui.objective.textContent=tr('status.walking');
  return true;
}

function rayAabb(origin,dir,box) {
  let tmin=-Infinity,tmax=Infinity;
  for(const axis of ['x','y','z']){
    if(Math.abs(dir[axis])<1e-6){if(origin[axis]<box.min[axis]||origin[axis]>box.max[axis])return false;continue;}
    let a=(box.min[axis]-origin[axis])/dir[axis], b=(box.max[axis]-origin[axis])/dir[axis];
    if(a>b)[a,b]=[b,a]; tmin=Math.max(tmin,a);tmax=Math.min(tmax,b);if(tmin>tmax)return false;
  }
  return tmax>=Math.max(tmin,0);
}
function pointerRay(clientX,clientY){
  const rect=canvas.getBoundingClientRect(),x=(clientX-rect.left)*canvas.width/rect.width,y=(clientY-rect.top)*canvas.height/rect.height;
  const near=camera.camera.screenToWorld(x,y,camera.camera.nearClip),far=camera.camera.screenToWorld(x,y,camera.camera.farClip);
  return {origin:near,dir:far.clone().sub(near).normalize()};
}
function updateHover(clientX,clientY) {
  hover=state===State.PLAYER&&rayAabb(pointerRay(clientX,clientY).origin,pointerRay(clientX,clientY).dir,elevatorBox);
  canvas.style.cursor=hover?'pointer':'default';
  ui.hotspot.style.display=hover||reveal?'flex':'none';
}
canvas.addEventListener('pointermove',e=>updateHover(e.clientX,e.clientY));
canvas.addEventListener('pointerdown',e=>{
  audio.start();
  const ray=pointerRay(e.clientX,e.clientY);
  if(rayAabb(ray.origin,ray.dir,elevatorBox))beginLiftInteraction();
});
ui.skip.addEventListener('click',()=>{audio.start();skipIntro();});
ui.audio_btn.addEventListener('click',()=>audio.start());
ui.language.addEventListener('change',async e=>{language=e.target.value;localStorage.setItem('departure-language',language);await loadLocale(language);});
ui.restart.addEventListener('click',()=>location.reload());
addEventListener('keydown',e=>{
  if(e.code==='Space'){e.preventDefault();reveal=e.type==='keydown';}
  if(e.key.toLowerCase()==='f')speedBoost=3;
  if(e.key.toLowerCase()==='p')ui.debug.style.display=ui.debug.style.display==='block'?'none':'block';
  if(e.key==='Escape')skipIntro();
});
addEventListener('keyup',e=>{if(e.code==='Space')reveal=false;if(e.key.toLowerCase()==='f')speedBoost=1;});
addEventListener('resize',()=>app.resizeCanvas());
if(params.has('debug'))ui.debug.style.display='block';
globalThis.__ADVENTURE__ = {
  engine: 'PlayCanvas',
  version: pc.version,
  getState: () => state,
  skipIntro,
  interactElevator: beginLiftInteraction,
  setLanguage: async code => {
    if (!['en','nl','es'].includes(code)) return false;
    language=code; ui.language.value=code; await loadLocale(code); return true;
  }
};

app.on('update',rawDt=>{
  const dt=Math.min(rawDt,.05)*speedBoost;elapsed+=dt;stateTime+=dt;frames++;
  if(elapsed>subtitleUntil)ui.subtitle.style.display='none';
  const desiredCamX=pc.math.clamp(hero.getPosition().x-2,-11,10);
  const cp=camera.getPosition();camera.setPosition(pc.math.lerp(cp.x,desiredCamX,Math.min(1,rawDt*1.8)),6.4,17);
  camera.lookAt(new pc.Vec3(camera.getPosition().x+3,2.05,0));
  puffs.forEach((p,i)=>{
    const t=(elapsed*p.speed+p.phase)%4.5, base=12+p.side*(2.5+(i%3)*.4);
    p.e.setPosition(base+p.side*Math.sin(t*1.7)*.5,.4+t*1.65,-10+(i%4)*.5);
    const s=.25+t*.23;p.e.setLocalScale(s,s*.75,s);
  });
  tower.findComponents('render').forEach((r,i)=>{if(r.entity.name==='gantry lamp')r.enabled=Math.floor(elapsed*2+i)%2===0;});
  if(state===State.INTRO_WALK&&walkTo(-7,2.25,dt))setState(State.HELMET);
  else if(state===State.HELMET){
    idlePose();
    const t=pc.math.clamp(stateTime/2.25,0,1), smooth=t*t*(3-2*t);
    helmet.enabled=visor.enabled=true;helmet.setLocalPosition(0,3.42-smooth*.75,.05);visor.setLocalPosition(0,3.4-smooth*.75,.38);
    rightArm.setLocalEulerAngles(-125*smooth,0,-18);leftArm.setLocalEulerAngles(-125*smooth,0,18);
    if(stateTime>2.55){idlePose();setState(State.INTRO_CONTINUE);}
  } else if(state===State.INTRO_CONTINUE&&walkTo(7,2.25,dt))setState(State.PLAYER);
  else if(state===State.WALK_TO_LIFT&&walkTo(targetX,3.15,dt)){idlePose();setState(State.ENTER);}
  else if(state===State.ENTER){
    hero.setPosition(targetX,hero.getPosition().y,pc.math.lerp(.5,-.5,Math.min(1,stateTime/1.25)));
    if(stateTime>1.3)setState(State.DOORS);
  } else if(state===State.DOORS){
    const t=pc.math.clamp(stateTime/1.45,0,1),x=1.42*(1-t);
    doorL.setLocalPosition(-x,2.35,1.65);doorR.setLocalPosition(x,2.35,1.65);
    if(stateTime>1.65)setState(State.ASCENT);
  } else if(state===State.ASCENT){
    lift.setPosition(17,.25+stateTime*4.5,0);
    if(stateTime>3.1)setState(State.COMPLETE);
  }
  const liftScreen=camera.camera.worldToScreen(new pc.Vec3(17,4.8,1.8));
  const rect=canvas.getBoundingClientRect(),sx=liftScreen.x/canvas.width*rect.width,sy=liftScreen.y/canvas.height*rect.height;
  ui.hotspot.style.left=`${sx}px`;ui.hotspot.style.top=`${sy}px`;
  ui.reveal.style.setProperty('--rx',`${sx}px`);ui.reveal.style.setProperty('--ry',`${sy}px`);
  ui.reveal.style.opacity=reveal&&state===State.PLAYER?'1':'0';
  if(reveal&&state===State.PLAYER)ui.hotspot.style.display='flex';
  statTime+=rawDt;
  if(statTime>.5){
    fpsSmooth=Math.round(frames/statTime);
    const dc=app.stats?.drawCalls?.total??'—', tris=app.stats?.frame?.triangles??app.stats?.frame?.trianglesRendered??'—';
    ui.debug.textContent=`PLAYCANVAS ${pc.version}\nSTATE  ${state}\nFPS    ${fpsSmooth}\nFRAME  ${(1000/Math.max(1,fpsSmooth)).toFixed(1)} ms\nDRAWS  ${dc}\nTRIS   ${tris}\nQUALITY ${quality.toUpperCase()}`;
    frames=0;statTime=0;
  }
});

setTimeout(()=>{ui.loading.style.opacity='0';setTimeout(()=>ui.loading.remove(),650);},450);
